import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MapPin, Clock, ChefHat, Heart, Award, Users } from "lucide-react";
import { Link } from "react-router-dom";

const About = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 shadow-wave">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <ArrowLeft className="h-6 w-6 text-primary" />
              <img src="./images/Logo restaurant .jpeg" alt="Logo La Baleine Bleue" className="h-10 w-10 object-contain" />
              <h1 className="text-2xl font-bold text-primary">La Baleine Bleue</h1>
            </Link>
            <div className="flex space-x-3 md:space-x-6 text-sm md:text-base">
              <Link to="/" className="text-foreground hover:text-primary transition-smooth">Accueil</Link>
              <Link to="/menu" className="text-foreground hover:text-primary transition-smooth">Menu</Link>
              <Link to="/about" className="text-primary font-medium">À propos</Link>
              <Link to="/contact" className="text-foreground hover:text-primary transition-smooth">Contact</Link>
            </div>
            <Link to="/contact">
              <Button className="ocean-gradient text-white shadow-ocean hover:shadow-wave transition-wave">
                Réserver
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Header */}
      <section className="pt-24 pb-12 wave-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <Badge className="mb-6 ocean-gradient text-white border-0">
              <Heart className="mr-2 h-4 w-4" />
              Notre Histoire
            </Badge>
            <h1 className="text-5xl font-bold mb-6 text-foreground">
              À propos de La Baleine Bleue
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Découvrez l'histoire de notre restaurant et la passion qui nous anime depuis des années
            </p>
          </div>
        </div>
      </section>

      {/* Restaurant Story */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-6 ocean-gradient text-white border-0">
                <MapPin className="mr-2 h-4 w-4" />
                Notre Restaurant
              </Badge>
              <h2 className="text-4xl font-bold mb-6 text-foreground">
                Un cadre exceptionnel face à la mer
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Situé au cœur de Saint Pierre La Mer, La Baleine Bleue vous accueille dans un cadre 
                authentique et chaleureux. Notre restaurant bénéficie d'une situation privilégiée 
                sur la place du port, offrant une vue imprenable sur la Méditerranée.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Avec sa terrasse ensoleillée et sa salle intérieure au décor maritime, 
                notre établissement vous invite à vivre une expérience culinaire unique 
                dans une ambiance décontractée et conviviale.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/menu">
                  <Button className="ocean-gradient text-white shadow-ocean hover:shadow-wave transition-wave">
                    <ChefHat className="mr-2 h-5 w-5" />
                    Découvrir notre menu
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-wave">
                    Nous contacter
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="./images/exterieur .jpeg" 
                alt="Restaurant La Baleine Bleue" 
                className="rounded-lg shadow-elegant w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-ocean">
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">15+</p>
                  <p className="text-sm text-muted-foreground">Années d'expérience</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chef Section */}
      <section className="py-20 wave-gradient">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <img 
                src="./images/interieur .jpeg" 
                alt="Intérieur du restaurant" 
                className="rounded-lg shadow-elegant w-full"
              />
            </div>
            <div className="order-1 lg:order-2">
              <Badge className="mb-6 ocean-gradient text-white border-0">
                <ChefHat className="mr-2 h-4 w-4" />
                Notre Chef
              </Badge>
              <h2 className="text-4xl font-bold mb-6 text-foreground">
                Philippe Lung, Spécialiste Choucroute de la Mer et Cordon Bleu
              </h2>
              <p className="text-lg text-muted-foreground mb-6 leading-relaxed">
                Passionné par la choucroute de la mer et le cordon bleu depuis son plus jeune âge, Philippe Lung 
                a développé son savoir-faire uniquement dans ce restaurant. 
                C'est grâce à son expérience qu'aujourd'hui ses plats sont les meilleurs.
              </p>
              <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                Sa spécialité, la <strong className="text-primary">Choucroute de la Mer</strong>, 
                est devenue la signature de notre restaurant. Un plat unique qui marie tradition 
                alsacienne et richesses méditerranéennes, créant une harmonie de saveurs exceptionnelle.
              </p>
              
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center">
                  <Award className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground">Spécialiste</p>
                  <p className="text-sm text-muted-foreground">Fruits de mer</p>
                </div>
                <div className="text-center">
                  <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                  <p className="font-semibold text-foreground">Équipe</p>
                  <p className="text-sm text-muted-foreground">Expérimentée</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 ocean-gradient text-white border-0">
              <Heart className="mr-2 h-4 w-4" />
              Nos Valeurs
            </Badge>
            <h2 className="text-4xl font-bold mb-6 text-foreground">
              Ce qui nous anime
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Découvrez les valeurs qui guident notre travail au quotidien
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="shadow-elegant border-0 text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 ocean-gradient rounded-full flex items-center justify-center mx-auto mb-6">
                  <ChefHat className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-foreground">Qualité</h3>
                <p className="text-muted-foreground">
                  Nous sélectionnons rigoureusement nos produits auprès de pêcheurs locaux 
                  pour vous garantir la fraîcheur et la qualité de nos fruits de mer.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-elegant border-0 text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 ocean-gradient rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-foreground">Passion</h3>
                <p className="text-muted-foreground">
                  Chaque plat est préparé avec amour et passion. Notre équipe met tout son 
                  cœur à vous offrir une expérience culinaire mémorable.
                </p>
              </CardContent>
            </Card>

            <Card className="shadow-elegant border-0 text-center">
              <CardContent className="p-8">
                <div className="w-16 h-16 ocean-gradient rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-4 text-foreground">Convivialité</h3>
                <p className="text-muted-foreground">
                  Nous créons une atmosphère chaleureuse et accueillante où chaque client 
                  se sent comme chez lui, dans un cadre détendu face à la mer.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Specialties Highlight */}
      <section className="py-20 wave-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 ocean-gradient text-white border-0">
              <Award className="mr-2 h-4 w-4" />
              Notre Spécialité
            </Badge>
            <h2 className="text-4xl font-bold mb-6 text-foreground">
              La Choucroute de la Mer
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Notre plat signature unique en son genre : une choucroute traditionnelle revisitée 
              avec les meilleurs fruits de mer de la Méditerranée. Filet de Loup, Saumon, 
              Saint-Pierre, Moules et Gambas s'harmonisent parfaitement sur un lit de choucroute 
              délicatement parfumée.
            </p>
            
            <div className="relative max-w-2xl mx-auto">
              <img 
                src="./images/choucroute .jpeg" 
                alt="Choucroute de la Mer" 
                className="rounded-lg shadow-elegant w-full"
              />
              <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full">
                <span className="text-lg font-bold text-primary">28,90€</span>
              </div>
            </div>
            
            <div className="mt-8">
              <Link to="/menu">
                <Button size="lg" className="ocean-gradient text-white shadow-ocean hover:shadow-wave transition-wave">
                  Voir notre menu complet
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Card className="shadow-elegant border-0 ocean-gradient text-white text-center">
            <CardContent className="p-12">
              <h2 className="text-4xl font-bold mb-6">
                Venez nous rendre visite !
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Réservez votre table et découvrez l'authenticité de notre cuisine marine
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-elegant transition-wave">
                    <Clock className="mr-2 h-5 w-5" />
                    Réserver maintenant
                  </Button>
                </Link>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary transition-wave">
                  <MapPin className="mr-2 h-5 w-5" />
                  3 place du port, Saint Pierre La Mer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Fahren Section */}
      <section className="py-20 wave-gradient">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-6 ocean-gradient text-white border-0">
              <Users className="mr-2 h-4 w-4" />
              Animations Musicales
            </Badge>
            <h2 className="text-4xl font-bold mb-6 text-foreground">
              Fahren, notre chanteuse
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
              Venez découvrir les performances de Fahren, notre talentueuse chanteuse qui vous accompagne 
              lors de vos repas avec ses mélodies envoûtantes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-foreground">Programmation</h3>
              <div className="space-y-4">
                <Card className="p-4 border-l-4 border-primary">
                  <h4 className="font-semibold text-foreground mb-2">Hors saison d'été</h4>
                  <p className="text-muted-foreground">
                    Tous les 15 jours le <strong>dimanche midi</strong>, Fahren vient enchanter 
                    votre déjeuner avec ses chansons.
                  </p>
                </Card>
                <Card className="p-4 border-l-4 border-accent">
                  <h4 className="font-semibold text-foreground mb-2">En saison d'été</h4>
                  <p className="text-muted-foreground">
                    Tous les <strong>mardis soir</strong>, profitez d'un dîner en musique 
                    avec les performances de Fahren.
                  </p>
                </Card>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
              <img 
                src="./images/Fahren 1 .jpeg" 
                alt="Fahren chanteuse 1" 
                className="rounded-lg shadow-elegant w-full h-48 object-cover"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <img 
              src="./images/Fahren 2 .png" 
              alt="Fahren chanteuse 2" 
              className="rounded-lg shadow-elegant w-full h-64 object-cover"
            />
            <img 
              src="./images/Fahren 3 .png" 
              alt="Fahren chanteuse 3" 
              className="rounded-lg shadow-elegant w-full h-64 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-primary text-white py-12">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <h3 className="text-2xl font-bold">La Baleine Bleue</h3>
            </div>
            <p className="text-white/80 mb-6">
              Restaurant La Baleine Bleue • Chef Philippe Lung • Saint Pierre La Mer
            </p>
            <div className="flex justify-center space-x-6">
              <a href="#" className="text-white/80 hover:text-white transition-smooth">Facebook</a>
              <a href="#" className="text-white/80 hover:text-white transition-smooth">Instagram</a>
              <a href="#" className="text-white/80 hover:text-white transition-smooth">TripAdvisor</a>
            </div>
            <div className="mt-8 pt-8 border-t border-white/20">
              <p className="text-white/60 text-sm">
                © 2024 La Baleine Bleue. Tous droits réservés.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;